import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faFlask, faPen, faPlay, faTrash } from '@fortawesome/free-solid-svg-icons';

import {
  STATUS_LABELS,
  formatDate,
  formatRelative,
  metaDraft,
  metaPayload,
  progressPercent,
  resultOf,
  sessionTitle,
} from './sessionHistory';

/**
 * UNE SÉANCE DANS L'HISTORIQUE, et sa fiche.
 *
 * LA FICHE EST DANS LA LIGNE, pas sur un écran à part : l'effectif d'un
 * atelier et ses enseignements se notent en repensant à la séance, donc en la
 * relisant. Un formulaire ailleurs obligerait à retrouver de quelle séance on
 * parle, ce que l'animateur venait précisément de faire.
 */

const Meta = ({ children }) => <span className="history-meta-item">{children}</span>;

const SessionCard = ({ session, onOpen, onSave, onArchive }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => metaDraft(session));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // La suppression se confirme DANS LA LIGNE, pas dans une boîte du navigateur :
  // le texte doit pouvoir dire ce qui se passe vraiment (retirée de
  // l'historique, conservée en base), et rester sous les yeux de la séance
  // concernée.
  const [confirming, setConfirming] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState(null);

  const percent = progressPercent(session);
  const result = resultOf(session);
  const finished = session.status === 'terminee';
  const participants = session.context?.nb_participants;

  const openFiche = () => {
    // Le brouillon est repris de la séance À CHAQUE OUVERTURE : la ligne peut
    // avoir été rechargée depuis la dernière fois qu'on l'a fermée.
    setDraft(metaDraft(session));
    setError(null);
    setEditing(true);
  };

  const field = (key) => ({
    value: draft[key],
    onChange: (event) => setDraft((current) => ({ ...current, [key]: event.target.value })),
  });

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const message = await onSave(session.game_id, metaPayload(draft));
    setSaving(false);
    if (message) setError(message);
    else setEditing(false);
  };

  const archive = async () => {
    setArchiving(true);
    setArchiveError(null);
    const message = await onArchive(session.game_id);
    // En cas de succès la ligne est rechargée et disparaît : rien à remettre.
    if (message) {
      setArchiving(false);
      setArchiveError(message);
    }
  };

  return (
    <div className={`history-card${session.is_test ? ' history-card-test' : ''}`}>
      <div className="history-card-head">
        <div>
          <div className="history-card-title">{sessionTitle(session)}</div>
          <div className="history-card-date">
            {formatDate(session.created_at)}
            {/* La dernière activité n'est montrée que si elle explique quelque
                chose : sur une séance terminée, elle ferait doublon avec la
                date de séance. */}
            {!finished && session.updated_at && (
              <span className="history-muted"> · activité {formatRelative(session.updated_at)}</span>
            )}
          </div>
        </div>
        <div className="history-card-badges">
          {session.is_test && (
            <span className="history-pill history-pill-test">
              <FontAwesomeIcon icon={faFlask} /> Test
            </span>
          )}
          <span className={`history-pill history-pill-${session.status}`}>
            {STATUS_LABELS[session.status] || session.status}
          </span>
        </div>
      </div>

      <div className="history-meta">
        <Meta>{session.config_name || session.config_id}</Meta>
        {participants != null && <Meta>{participants} participants</Meta>}
        <Meta>{session.nb_decisions} mesures</Meta>
        {session.nb_actors > 0 && <Meta>{session.nb_actors} rôles</Meta>}
        {session.territory_id && <Meta>{session.territory_id}</Meta>}
        {session.context?.client && <Meta>{session.context.client}</Meta>}
        {session.context?.location && <Meta>{session.context.location}</Meta>}
      </div>

      {/* La progression n'a de sens que sur une séance inachevée : à 100 %,
          l'étiquette « Terminée » le dit déjà. */}
      {!finished && percent != null && (
        <div className="history-progress">
          <div className="history-progress-track">
            <div className="history-progress-fill" style={{ width: `${percent}%` }} />
          </div>
          <span className="history-progress-label">
            {session.nb_pages_played}/{session.nb_pages_total} pages
          </span>
        </div>
      )}

      <div className="history-card-foot">
        {result && (
          <span className="history-result">
            {result.label} <strong>{result.value}</strong>
          </span>
        )}
        <div className="history-card-actions">
          <button className="history-button" onClick={() => onOpen(session)}>
            <FontAwesomeIcon icon={finished ? faChartLine : faPlay} />
            {finished ? ' Revoir le bilan' : ' Reprendre'}
          </button>
          <button className="history-button history-button-ghost" onClick={() => (editing ? setEditing(false) : openFiche())}>
            <FontAwesomeIcon icon={faPen} /> Fiche
          </button>
          {onArchive && !confirming && (
            <button
              className="history-button history-button-ghost history-button-danger"
              onClick={() => { setArchiveError(null); setConfirming(true); }}
              title="Retirer cette séance de l'historique"
            >
              <FontAwesomeIcon icon={faTrash} /> Supprimer
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <div className="history-confirm">
          <span>
            Retirer cette séance de votre historique ? Elle ne comptera plus dans vos
            statistiques. Elle reste conservée en base.
          </span>
          {archiveError && <div className="history-error">{archiveError}</div>}
          <div className="history-fiche-actions">
            <button className="history-button history-button-danger" onClick={archive} disabled={archiving}>
              {archiving ? 'Suppression…' : 'Confirmer la suppression'}
            </button>
            <button
              className="history-button history-button-ghost"
              onClick={() => setConfirming(false)}
              disabled={archiving}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {session.context?.notes && !editing && (
        <div className="history-notes">{session.context.notes}</div>
      )}

      {editing && (
        <form className="history-fiche" onSubmit={submit}>
          <div className="history-fiche-grid">
            <label>
              Nom de la séance
              <input type="text" maxLength={200} {...field('name')} />
            </label>
            <label>
              Participants
              <input type="number" min="0" max="10000" {...field('nb_participants')} />
            </label>
            <label>
              Client / organisation
              <input type="text" maxLength={200} {...field('client')} />
            </label>
            <label>
              Lieu
              <input type="text" maxLength={200} {...field('location')} />
            </label>
          </div>
          <label className="history-fiche-notes">
            Enseignements de la séance
            <textarea rows={3} maxLength={5000} {...field('notes')} />
          </label>
          <label className="history-fiche-check">
            <input
              type="checkbox"
              checked={draft.is_test}
              onChange={(event) => setDraft((current) => ({ ...current, is_test: event.target.checked }))}
            />
            Séance de test — exclue des statistiques et du classement des séances
          </label>
          {error && <div className="history-error">{error}</div>}
          <div className="history-fiche-actions">
            <button className="history-button" type="submit" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              className="history-button history-button-ghost"
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Annuler
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SessionCard;
